<template>
  <div>
    <div
      v-if="readingMode"
      class="columns is-centered header-columns is-mobile"
    >
      <div class="column is-4">
        <IntervalPlayer
          :src="section.audioUrl"
          :from="startTime"
          :to="selectedTextEndTime"
          :key="componentKey"
          @currentTime="currentTimeUpdate"
        />
      </div>
      <div class="column is-4">
        <RecordButton
          :disabled="!supportedBrowser"
          @recording="emitRecording"
        />
      </div>
      <div class="column is-4">
        <div>
          <b-button
            class="cut-button"
            :type="cutButtonType"
            :disabled="!section.wordAlignment"
            @click="clickCut"
            expanded
          >
            <p class="heading"></p>
            <p><b-icon pack="fas" icon="cut"></b-icon></p>
          </b-button>
        </div>
      </div>
    </div>
    <div v-if="dictationMode">
      <div class="columns is-centered is-vcentered header-columns is-mobile">
        <div class="column is-4-desktop">
          <IntervalPlayer
            :src="section.audioUrl"
            :from="startTime"
            :to="selectedTextEndTime"
            :key="componentKey"
            @currentTime="currentTimeUpdate"
          />
        </div>
        <div class="column is-4-desktop">
          <IntervalPlayer
            :src="section.audioUrl"
            :from="currentHiddenWordRegionStartTime"
            :to="selectedTextEndTime"
            playIcon="arrow-right"
            :key="componentKey"
            @currentTime="currentTimeUpdate"
          />
        </div>
      </div>
      <DictationInput
        :currentHiddenWord="currentHiddenWord"
        :currentHiddenWordId="currentHiddenWordId"
        :bookId="section.bookId"
      />
      <br />
    </div>
    <div
      v-if="readingMode && !supportedBrowser"
      class="has-text-centered has-text-danger is-size-6 is-size-7-mobile error"
    >
      It looks like your browser doesn't support recording audio. Try switching
      to a different browser or device.
    </div>
    <LiveText
      class="paragraph is-size-5-tablet is-size-6-mobile"
      :text="section.text"
      :wordAlignment="section.wordAlignment"
      :currentTime="currentTime"
      :textSelectionMode="cutting"
      :dictationMode="dictationMode"
      :obscurityLevel="obscurityLevel"
      :key="componentKey * internalComponentKey"
      @selectedTime="updateStartTime"
      @selectedText="updateSelectedText"
      @currentHiddenWord="updateCurrentHiddenWord"
    />
  </div>
</template>

<script>
import IntervalPlayer from "@/components/IntervalPlayer";
import LiveText from "@/components/LiveText";
import RecordButton from "@/components/RecordButton";
import DictationInput from "@/components/DictationInput";

export default {
  props: ["section", "componentKey", "mode", "obscurityLevel"],
  components: {
    IntervalPlayer,
    LiveText,
    RecordButton,
    DictationInput
  },
  data() {
    return {
      supportedBrowser: true,
      currentTime: 0,
      startTime: 0,
      selectedTextStartTime: 0,
      selectedTextEndTime: 0,
      selectedText: this.section.text,
      cutting: false,
      internalComponentKey: 11,
      currentHiddenWord: undefined,
      currentHiddenWordId: -1,
      currentHiddenWordRegionStartTime: 0,
      currentHiddenWordRegionEndTime: 0
    };
  },
  computed: {
    cutButtonType() {
      return this.cutting ? "is-warning" : "is-primary";
    },
    readingMode() {
      return this.mode == "Reading";
    },
    dictationMode() {
      return this.mode == "Dictation";
    }
  },
  methods: {
    clickCut() {
      if (this.cutting) this.resetTextAndTime();
      else this.cutting = true;
    },
    updateCurrentHiddenWord(word, wordId) {
      this.currentHiddenWord = word;
      this.currentHiddenWordId = wordId;
      this.updateCurrentHiddenWordRegion(wordId);
    },
    updateCurrentHiddenWordRegion(wordId) {
      const maxWordId = this.section.wordAlignment.words.length - 1;
      const firstWordId = wordId > 0 ? wordId - 1 : wordId;
      const lastWordId = wordId < maxWordId ? wordId + 1 : wordId;
      const firstWord = this.section.wordAlignment.words[firstWordId];
      const lastWord = this.section.wordAlignment.words[lastWordId];
      this.currentHiddenWordRegionStartTime = firstWord.startTime;
      this.currentHiddenWordRegionEndTime = lastWord.endTime;
    },
    updateSelectedText(selectedText, startTime, endTime) {
      this.selectedTextStartTime = startTime;
      this.selectedTextEndTime = endTime;
      this.updateStartTime(startTime);
      this.selectedText = selectedText;
    },
    updateStartTime(startTime) {
      if (
        startTime >= this.selectedTextStartTime &&
        (!this.selectedTextEndTime || startTime < this.selectedTextEndTime)
      ) {
        this.startTime = startTime;
        this.currentTime = startTime;
      }
    },
    currentTimeUpdate(currentTime) {
      this.currentTime = currentTime;
    },
    emitRecording(recording) {
      const result = {
        audioBlob: recording.audioBlob,
        audioUrl: recording.audioUrl,
        duration: recording.duration,
        text: this.selectedText,
        startTime: this.selectedTextStartTime,
        endTime: this.selectedTextEndTime
      };
      this.$emit("recording", result);
    },
    checkBrowserSupport() {
      const supportedBrowser = !!navigator?.mediaDevices;
      this.supportedBrowser = supportedBrowser;
      if (!supportedBrowser && window.umami) {
        window.umami("Browser doesn't support audio recording.");
      }
    },
    resetTextAndTime() {
      this.currentTime = 0;
      this.startTime = 0;
      this.selectedTextStartTime = 0;
      this.selectedTextEndTime = 0;
      this.selectedText = this.section.text;
      this.cutting = false;
      this.internalComponentKey += 1;
    }
  },
  watch: {
    mode() {
      this.resetTextAndTime();
    }
  },
  created() {
    this.checkBrowserSupport();
    this.resetTextAndTime();
  }
};
</script>

<style lang="scss" scoped>
.paragraph {
  padding-bottom: 10px;
}

.error {
  padding-bottom: 10px;
}

.cut-button {
  height: 100%;
  width: 100%;
}
</style>
